/**
 * "N activations" — the one thread from an audience to the syncs shipping it (ACT2-539).
 *
 * With activations a standalone, workspace-wide surface, an audience page carries no evidence that
 * anything is shipping it at all. This is the smallest honest repair: a COUNT, which is a fact the page
 * did not previously state, that doubles as the way in — and it lands on the list already narrowed to
 * this audience rather than on a workspace-wide list the reader then has to filter by hand.
 *
 * It deep-links into filter state the list already owns (`audienceActivationsPath` → `?f_audience=`),
 * which is why this direction needed no new list mode and no new procedure. The count comes from
 * `listSyncsForSource`, the same call the detail page uses, scoped to the one source this audience
 * mirrors to.
 *
 * **Absent at zero, and that is not the same as hidden.** Rule 10's "disable, don't hide" governs a
 * control whose action is unavailable for a system reason the reader should be told about. Here there
 * is no action being withheld: an audience nothing ships has no narrowed list to open, and a "0
 * activations" link would resolve to an empty page — the dead affordance the same rule's second half
 * rules out. The Activate button beside it is the affordance for that state, and it is always there.
 *
 * It renders nothing while the count is in flight, rather than a skeleton or a zero. The control is a
 * secondary fact in a header whose primary controls are already drawn, and a placeholder that resolves
 * to *nothing* on most audiences is a worse flicker than an element that simply arrives.
 *
 * **It belongs to the header's IDENTITY cluster — beside the title and the status badge — not to the
 * action cluster on the right.** It first shipped on the right, between the Size control and Activate,
 * and read there as an orphaned button: it was the one item in that row that is not a control, so it
 * borrowed the placement of a button without the affordance of one. What it actually states is a
 * property of the audience, the same kind of thing the status badge states — this audience is Active,
 * and three syncs ship it. Moving it left also resolves what remains on the right into one gradient,
 * control → action → menu, with nothing in it that merely reports.
 */
import { useQuery } from '@tanstack/react-query';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { useOrpc } from '@/_shared/hooks/useOrpc';
import { audienceActivationsPath } from '@/activations/lib/activation-list';

interface AudienceActivationsLinkProps {
  /**
   * The rETL source this audience mirrors to — `record.rsSourceId`. NOT the Lookout audience id: it is
   * both what `listSyncsForSource` takes and what the list's audience facet filters on. A caller with
   * a null `rsSourceId` (never pushed) must not render this at all.
   */
  rsSourceId: string;
}

export function AudienceActivationsLink({ rsSourceId }: AudienceActivationsLinkProps): React.ReactElement | null {
  const { wp } = useWorkspace();
  const orpc = useOrpc();

  const syncsQuery = useQuery({
    ...orpc.activations.listSyncsForSource.queryOptions({ input: { sourceId: rsSourceId } }),
    enabled: Boolean(rsSourceId),
  });

  const count = syncsQuery.data?.syncs.length ?? 0;
  // A failed count is indistinguishable from none HERE, on purpose. This is a secondary fact on a page
  // whose job is the definition; surfacing "could not count the activations" would spend a header slot
  // and a reader's attention on the failure of a link. The activations page reports its own errors.
  if (count === 0) return null;

  return (
    // A plain link, NOT a `Button variant="text"`. It sits in the identity cluster beside the title and
    // the status badge, where a button — even a text one — would read as a third control on a line that
    // has none. The recipe is `ActivationDetail`'s header link one step up in size: muted, hovering to
    // foreground, the arrow reversed because this one goes forward rather than back.
    //
    // `shrink-0` is load-bearing. The `<h1>` next to it is `min-w-0 truncate`, so without this the count
    // would share the shortfall with the title and a long audience name would clip "3 activati…".
    // The title is the thing that yields; a five-character fact is not.
    <Link
      to={wp(audienceActivationsPath(rsSourceId))}
      className="inline-flex shrink-0 items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
    >
      {/* The count IS the label — "3 activations", not "Activations (3)" — so the accessible name
          states the fact rather than naming a destination the arrow already implies. Singular matters:
          "1 activations" is the kind of thing a reader reads as a bug in the number. */}
      <span>
        {count} {count === 1 ? 'activation' : 'activations'}
      </span>
      {/* Decorative, and it must stay that way: the accessible name is "3 activations", not
          "3 activations, arrow right". It says the link leaves this page, which the words do not. */}
      <ArrowRight aria-hidden className="size-3.5" />
    </Link>
  );
}
